import { useRef, useState } from 'react';
import { EntityUser } from '@admiin-com/ds-graphql';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from '@admiin-com/ds-web';
import { useUpdateEntityUser } from '../../../../hooks/useUpdateEntityUser/useUpdateEntityUser';
import { useModal } from '../../../ModalManager/ModalContext';
import { useFirmEntityUsers } from '../../../../hooks/useFirmEntityUsers/useFirmEntityUsers';

export const useLinkEntity = (clientUser: EntityUser) => {
  const showSnackbar = useSnackbar();

  const { mutate: updateEntityUser } = useUpdateEntityUser({
    entityId: clientUser.entityId,
  });
  const { t } = useTranslation();
  const { openModal, closeModal } = useModal();
  // Modal states
  const confirmDialogId = useRef<string | null>(null);
  const [loading, setLoading] = useState(false);

  const { refetch: refetchEntityUsers } = useFirmEntityUsers({
    skip: true,
    clientGroupCode: clientUser?.clientGroupCode || '',
  });

  // Handle linking entity
  const handleLinkEntity = async (
    entityUser: EntityUser,
    clientUser: EntityUser
  ) => {
    if (!clientUser?.clientCode) {
      showSnackbar({
        message: t('noClientCodeError', {
          ns: 'clients',
          defaultValue: 'Current client has no client code',
        }),
        severity: 'error',
      });
      return;
    }
    // Check if the selected entity already has a different clientCode
    if (
      entityUser.clientCode &&
      entityUser.clientCode !== clientUser?.clientCode
    ) {
      return new Promise((resolve) => {
        confirmDialogId.current = openModal('confirmDialog', {
          title: t('confirmLinkOverwrite', {
            ns: 'clients',
            defaultValue: 'Confirm Link Overwrite',
          }),
          message: t('confirmLinkOverwriteMessage', {
            ns: 'clients',
            defaultValue:
              'This entity is already linked to client group "{{existingCode}}". Linking it to a new group will overwrite the existing link.',
            existingCode: entityUser.clientCode,
          }),
          onOK: async () => {
            const result = await linkEntity(entityUser, clientUser);
            resolve(result);
          },
          onCancel: async () => resolve(undefined),
        });
      });
    } else {
      // No existing clientCode or same clientCode, proceed with linking
      return await linkEntity(entityUser, clientUser);
    }
  };

  const linkEntity = async (entityUser: EntityUser, clientUser: EntityUser) => {
    try {
      setLoading(true);
      const updatedEntityUser = await updateEntityUser({
        variables: {
          input: {
            entityId: entityUser.entityId,
            userId: entityUser.userId,
            linkingEntityId: clientUser.entityId,
            linkingUserId: clientUser.userId,
            // clientCode: clientUser.clientCode,
            clientGroupCode: clientUser.clientGroupCode,
          },
        },
      });

      showSnackbar({
        message: t('entityLinkedSuccessfully', {
          ns: 'clients',
          defaultValue: 'Entity linked successfully',
          entityName: entityUser.entity?.name || entityUser.entitySearchName,
        }),
        severity: 'success',
      });

      await refetchEntityUsers();

      // Close modals
      if (confirmDialogId.current) {
        closeModal(confirmDialogId.current);
      }
      return updatedEntityUser?.data?.updateEntityUser;
    } catch (error: any) {
      console.error('Error linking entity:', error);
      showSnackbar({
        message: t(error?.message, {
          ns: 'errors',
        }),
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    handleLinkEntity,
    loading,
  };
};
